import { FolderExportJobResponse } from "../services/documentService";

const GUID_PATTERN =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;

export const FOLDER_EXPORT_QUEUED_STATUS = 0;
export const FOLDER_EXPORT_RUNNING_STATUS = 1;

export const FOLDER_EXPORT_IN_PROGRESS_TOOLTIP =
  "An export job is already queued or running for this scope. Please wait until it completes.";

export function isFolderExportJobActive(status: number): boolean {
  return status === FOLDER_EXPORT_QUEUED_STATUS || status === FOLDER_EXPORT_RUNNING_STATUS;
}

export function normalizeAssemblyType(assemblyType: string): string {
  const normalized = assemblyType.trim().toLowerCase();

  if (normalized === "engine") {
    return "Engine";
  }

  if (normalized === "apu") {
    return "APU";
  }

  if (
    normalized === "landinggear"
    || normalized === "landing-gear"
    || normalized === "landing_gear"
  ) {
    return "LandingGear";
  }

  return assemblyType.trim();
}

export function normalizeBreadcrumbPath(folderPath?: string): string {
  if (!folderPath?.trim()) {
    return "";
  }

  const normalized = folderPath.replace(/\\/g, "/").replace(/^\/+|\/+$/g, "");
  if (!normalized) {
    return "";
  }

  const segments = normalized.split("/").filter(Boolean);
  while (segments.length > 0 && GUID_PATTERN.test(segments[0])) {
    segments.shift();
  }

  return segments.join(" / ");
}

export function buildAirframeRootExportLabel(tailNumber: string): string {
  const tail = tailNumber.trim();
  return tail ? `Airframe / ${tail} / Root` : "Airframe";
}

export function buildAssemblyRootExportLabel(
  assemblyType: string,
  assemblyFolder: string,
): string {
  const folder = assemblyFolder.trim();
  const type = assemblyType.trim();

  if (folder && type) {
    return `Assembly / ${normalizeAssemblyType(type)} / ${folder} / Root`;
  }

  return "Assembly / Root";
}

export function buildFolderPathExportLabel(params: {
  folderPath?: string;
  tailNumber?: string;
  assemblyType?: string;
  assemblyFolder?: string;
}): string {
  const normalizedPath = normalizeBreadcrumbPath(params.folderPath);
  const tail = params.tailNumber?.trim();
  const assemblyType = params.assemblyType?.trim();
  const assemblyFolder = params.assemblyFolder?.trim();

  if (tail) {
    return normalizedPath
      ? `Airframe / ${tail} / ${normalizedPath}`
      : `Airframe / ${tail} / Folder`;
  }

  if (assemblyType && assemblyFolder) {
    const basePath = `Assembly / ${normalizeAssemblyType(assemblyType)} / ${assemblyFolder}`;
    return normalizedPath ? `${basePath} / ${normalizedPath}` : basePath;
  }

  return normalizedPath ? `Folder / ${normalizedPath}` : "Folder Export";
}

export function jobMatchesExportLabel(
  job: FolderExportJobResponse,
  expectedLabel: string,
): boolean {
  if (!isFolderExportJobActive(job.status)) {
    return false;
  }

  const label = job.exportLabel?.trim();
  const expected = expectedLabel.trim();
  return Boolean(label && expected && label === expected);
}

export function findActiveExportJobByLabel(
  jobs: FolderExportJobResponse[],
  expectedLabel: string,
): FolderExportJobResponse | undefined {
  return jobs.find((job) => jobMatchesExportLabel(job, expectedLabel));
}
